(() => {
  const stateByFilter = new Map();
  let availabilityDefaultResolved = false;
  let availabilityApplyPending = false;

  const selector = [
    '.facets--vertical accordion-custom.facets__item',
    '.facets--drawer accordion-custom.facets__item',
  ].join(',');

  const normalize = (value = '') =>
    value
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();

  const getMode = (wrapper) => (wrapper.closest('.facets--drawer') ? 'drawer' : 'vertical');

  const getLabel = (wrapper) =>
    wrapper.querySelector('.facets__summary .facets__label')?.textContent?.trim() || '';

  const getFilterId = (wrapper) => {
    const param = wrapper.dataset.filterParamName || '';
    const label = normalize(getLabel(wrapper));
    return `${getMode(wrapper)}:${param || label}`;
  };

  const isAvailabilityFilter = (wrapper) => {
    const param = normalize(wrapper.dataset.filterParamName || '');
    const label = normalize(getLabel(wrapper));
    return param.includes('availability') || label === 'disponibilidade' || label === 'availability';
  };

  const isDefaultOpenFilter = (wrapper) => {
    const param = normalize(wrapper.dataset.filterParamName || '');
    const isPrice = Boolean(wrapper.querySelector('[data-testid="price-filter"]')) || param.includes('price');
    return isPrice || isAvailabilityFilter(wrapper);
  };

  const supportsDefaultAvailability = () => {
    const path = window.location.pathname.replace(/\/+$/, '').toLowerCase();
    const isCollection = path.startsWith('/collections/') || path.includes('/collections/');
    const isSearch = path === '/search' || path.endsWith('/search');

    return isCollection || isSearch;
  };

  const getInputLabel = (wrapper, input) => {
    const directLabel = normalize(input.dataset.label || '');
    if (directLabel) return directLabel;

    if (input.id) {
      const escapedId = typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
        ? CSS.escape(input.id)
        : input.id.replace(/(["\\])/g, '\\$1');
      const label = wrapper.querySelector(`label[for="${escapedId}"]`);
      if (label) return normalize(label.textContent || '');
    }

    return normalize(input.closest('label')?.textContent || '');
  };

  const getInStockInput = (wrapper) => {
    if (!isAvailabilityFilter(wrapper)) return null;

    const inputs = Array.from(wrapper.querySelectorAll('input[type="checkbox"][name]'));

    return (
      inputs.find((input) => {
        const label = getInputLabel(wrapper, input);
        return label === 'em estoque' || label === 'in stock';
      }) ||
      inputs.find((input) => String(input.value) === '1') ||
      null
    );
  };

  const availabilityHasExplicitState = (wrapper, inStockInput) => {
    const params = new URL(window.location.href).searchParams;
    const hasAvailabilityInUrl = params.has(inStockInput.name);
    const hasCheckedAvailability = Array.from(
      wrapper.querySelectorAll('input[type="checkbox"][name]')
    ).some((input) => input.checked);

    return hasAvailabilityInUrl || hasCheckedAvailability;
  };

  const bindAvailabilityIntent = (wrapper) => {
    if (!isAvailabilityFilter(wrapper) || wrapper.dataset.pdlAvailabilityIntentBound === 'true') return;

    wrapper.addEventListener('change', (event) => {
      if (!(event.target instanceof HTMLInputElement)) return;
      if (event.target.type !== 'checkbox') return;
      availabilityDefaultResolved = true;
      availabilityApplyPending = false;
    });

    wrapper.dataset.pdlAvailabilityIntentBound = 'true';
  };

  const setupDefaultAvailability = (wrapper) => {
    if (!supportsDefaultAvailability() || !isAvailabilityFilter(wrapper)) return;

    bindAvailabilityIntent(wrapper);

    const inStockInput = getInStockInput(wrapper);
    if (!inStockInput || availabilityDefaultResolved || availabilityApplyPending) return;

    if (availabilityHasExplicitState(wrapper, inStockInput)) {
      availabilityDefaultResolved = true;
      return;
    }

    availabilityApplyPending = true;

    customElements
      .whenDefined('facet-inputs-component')
      .then(() => {
        if (availabilityDefaultResolved) {
          availabilityApplyPending = false;
          return;
        }

        if (!inStockInput.isConnected) {
          availabilityApplyPending = false;
          scheduleFullInitialize();
          return;
        }

        const component = inStockInput.closest('facet-inputs-component');
        if (!component || typeof component.updateFilters !== 'function') {
          availabilityApplyPending = false;
          return;
        }

        inStockInput.checked = true;
        availabilityDefaultResolved = true;
        availabilityApplyPending = false;
        component.updateFilters();
      })
      .catch(() => {
        availabilityApplyPending = false;
      });
  };

  const setDefaultAttributes = (wrapper, shouldOpen) => {
    const attributes = ['open-by-default-on-desktop', 'open-by-default-on-mobile'];

    attributes.forEach((attribute) => {
      if (shouldOpen) wrapper.setAttribute(attribute, '');
      else wrapper.removeAttribute(attribute);
    });
  };

  const bindFilter = (wrapper) => {
    if (!(wrapper instanceof HTMLElement)) return;

    const details = wrapper.querySelector('details.facets__panel');
    if (!(details instanceof HTMLDetailsElement)) return;

    const filterId = getFilterId(wrapper);
    const isPriority = isDefaultOpenFilter(wrapper);
    const shouldOpen = stateByFilter.has(filterId) ? stateByFilter.get(filterId) : isPriority;

    wrapper.dataset.pdlPriorityFilter = isPriority ? 'true' : 'false';
    setDefaultAttributes(wrapper, shouldOpen);

    if (details.open !== shouldOpen) {
      details.open = shouldOpen;
    }

    setupDefaultAvailability(wrapper);

    if (wrapper.dataset.pdlFacetsBound === 'true') return;

    details.addEventListener('toggle', () => {
      stateByFilter.set(filterId, details.open);
    });

    wrapper.dataset.pdlFacetsBound = 'true';
  };

  const initialize = (root = document) => {
    if (root instanceof Element && root.matches(selector)) bindFilter(root);
    root.querySelectorAll?.(selector).forEach(bindFilter);
  };

  const initializeAddedNode = (node) => {
    if (!(node instanceof Element)) return;
    initialize(node);
  };

  let frame = 0;
  const scheduleFullInitialize = () => {
    if (frame) return;

    frame = window.requestAnimationFrame(() => {
      frame = 0;
      initialize(document);
    });
  };

  const start = () => {
    initialize(document);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach(initializeAddedNode);
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });

    document.addEventListener('shopify:section:load', scheduleFullInitialize);
    window.addEventListener('pageshow', scheduleFullInitialize);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
